import axios, { AxiosRequestConfig } from "axios"
import {
  getBBUserFromLocalStorage,
  setBBUserInLocalStorage
} from "./localStorage"

let store: any

export const injectStore = (_store: any) => {
  store = _store
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST ?? "/",
  withCredentials: true
})

// Add request interceptor to add auth token to all requests
api.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const user = getBBUserFromLocalStorage()
    if (user?.sessid && config.headers) {
      config.headers.Authorization = user.sessid
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  response => {
    // The API echoes the live Drupal session id on authenticated responses.
    // Session ids rotate server-side (login, masquerade switch/back), so adopt
    // the new id or every subsequent request authenticates with a dead token.
    // Never adopt from an error response — its session id belongs to a
    // failed/anonymous request and would poison the stored credential.
    // Never adopt from a response whose request was sent with a token other
    // than the one stored now: it was in flight across a login/masquerade
    // transition, so its echo names the PREVIOUS session and would overwrite
    // the fresh credential with a dead one (stranding a masquerade).
    const liveSessid = response.headers?.["x-bb-sessid"]
    if (liveSessid && response.data?.status !== "error") {
      const user = getBBUserFromLocalStorage()
      const sentSessid = response.config?.headers?.Authorization
      if (
        user?.sessid &&
        user.sessid !== liveSessid &&
        sentSessid === user.sessid
      ) {
        setBBUserInLocalStorage({ ...user, sessid: liveSessid })
      }
    }
    return response
  },
  error => {
    if (error.response && error.response.status === 401) {
      store.dispatch({ type: "auth/sessionExpired" })
    }
    return Promise.reject(error)
  }
)

export default api

export const getQueryString = (params: {
  [key: string]: number | string | boolean | undefined
}): string => {
  const str = Object.keys(params ?? {})
    .filter(
      k => params[k] !== null && params[k] !== undefined && params[k] !== ""
    )
    .map(
      k => `${k}=${encodeURIComponent(params[k] as string | number | boolean)}`
    )
    .join("&")

  return str ? `?${str}` : ""
}
